// Contexte pour communiquer avec les modules système
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useApiHealth } from '../Hooks/useApiHealth';
import { SystemNotificationService } from '../Services/SystemNotificationService';
import type { SystemNotification, Announcement } from './types';

interface SystemModulesContextValue {
    // État API
    apiStatus: {
        isOnline: boolean;
        lastCheck: Date | null;
        latency?: number;
    };

    // Notifications
    notifications: SystemNotification[];
    dismissNotification: (id: string) => void;
    clearNotifications: () => void;

    // Annonces
    announcements: Announcement[];
    dismissAnnouncement: (id: string) => void;
}

const SystemModulesContext = createContext<SystemModulesContextValue | null>(null);

interface SystemModulesProviderProps {
    children: React.ReactNode;
    apiUrl: string;
    apiEnabled?: boolean;
}

/**
 * Provider pour les modules système
 * Fournit l'état API, les notifications et les annonces
 */
export function SystemModulesProvider({
    children,
    apiUrl,
    apiEnabled = true
}: SystemModulesProviderProps) {
    // État API avec useApiHealth
    const apiStatus = useApiHealth({
        apiUrl,
        enabled: apiEnabled,
        checkInterval: 30000 // 30 secondes
    });

    // Notifications
    const [notifications, setNotifications] = useState<SystemNotification[]>([]);

    // Annonces (exemple statique, pourrait venir de l'API)
    const [announcements] = useState<Announcement[]>([]);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(
        () => {
            const saved = localStorage.getItem('dismissedAnnouncements');
            return saved ? new Set(JSON.parse(saved)) : new Set();
        }
    );

    // S'abonner aux notifications du service
    useEffect(() => {
        const unsubscribe = SystemNotificationService.subscribe((newNotifications) => {
            setNotifications(newNotifications);
        });

        // Charger les notifications initiales
        setNotifications(SystemNotificationService.getAll());

        return unsubscribe;
    }, []);

    // Gérer les erreurs d'API offline
    useEffect(() => {
        if (!apiStatus.isOnline && apiStatus.lastCheck && apiEnabled) {
            // Ne notifier qu'une fois
            const hasNotification = notifications.some(
                n => n.message.includes('API hors ligne')
            );
            
            if (!hasNotification) {
                SystemNotificationService.warning(
                    'API hors ligne - Les données locales sont utilisées',
                    0 // Pas d'auto-dismiss
                );
            }
        }
    }, [apiStatus.isOnline, apiStatus.lastCheck, apiEnabled]);

    const dismissNotification = useCallback((id: string) => {
        SystemNotificationService.dismiss(id);
    }, []);

    const clearNotifications = useCallback(() => {
        SystemNotificationService.clear();
    }, []);

    const dismissAnnouncement = useCallback((id: string) => {
        const newDismissed = new Set(dismissedAnnouncements);
        newDismissed.add(id);
        setDismissedAnnouncements(newDismissed);
        localStorage.setItem('dismissedAnnouncements', JSON.stringify([...newDismissed]));
    }, [dismissedAnnouncements]);

    // Filtrer les annonces non-dismissées
    const visibleAnnouncements = announcements.filter(
        a => !dismissedAnnouncements.has(a.id)
    );

    const value: SystemModulesContextValue = {
        apiStatus,
        notifications,
        dismissNotification,
        clearNotifications,
        announcements: visibleAnnouncements,
        dismissAnnouncement
    };

    return (
        <SystemModulesContext.Provider value={value}>
            {children}
        </SystemModulesContext.Provider>
    );
}

/**
 * Hook pour accéder au contexte des modules système
 */
export function useSystemModules(): SystemModulesContextValue {
    const context = useContext(SystemModulesContext);
    
    if (!context) {
        throw new Error(
            'useSystemModules doit être utilisé dans un SystemModulesProvider'
        );
    }
    
    return context;
}


